/**
 * Rapportgenerator: mallar från tilldelade datamodeller, CSV-nedladdning.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useTranslation } from '@/store/languageStore';
import api from '@/services/api/client';
import { ROUTES } from '@/constants/routes';
import OrgTreeSelect from '@/components/common/OrgTreeSelect';

interface ReportField {
  key: string;
  labelKey: string;
  group: string;
  kind: 'snapshot' | 'period';
}

interface ReportModel {
  code: string;
  nameKey: string;
  fields: ReportField[];
}

interface TemplateRecord {
  id: number;
  name: string;
  modelCode: string;
  fieldKeys: string[];
  filters: { activeOnly: boolean };
  dataWindow: { type: string };
  schedule: { type: 'monthly' | 'yearly'; dayOfMonth: number; month?: number } | null;
  recipientUserIds: string[];
}

interface Recipient {
  id: string;
  name: string;
  email: string;
}

const GROUPS = [
  'vehicle',
  'organization',
  'economy',
  'placement',
  'charging',
  'booking',
  'notes',
  'current',
  'period',
  'fuelCards',
  'service',
  'tires',
  'attachments',
  'equipment',
] as const;

function apiError(error: unknown, fallback: string): string {
  return (error as { response?: { data?: { error?: string } } })?.response?.data?.error || fallback;
}

export default function ReportGeneratorPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [organizationId, setOrganizationId] = useState('');
  const [models, setModels] = useState<ReportModel[]>([]);
  const [templates, setTemplates] = useState<TemplateRecord[]>([]);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [modelCode, setModelCode] = useState('');
  const [fieldKeys, setFieldKeys] = useState<string[]>(['registration_number']);
  const [activeOnly, setActiveOnly] = useState(true);
  const [windowType, setWindowType] = useState('snapshot');
  const [scheduleMode, setScheduleMode] = useState<'manual' | 'monthly' | 'yearly'>('manual');
  const [dayOfMonth, setDayOfMonth] = useState(3);
  const [month, setMonth] = useState(1);
  const [recipientIds, setRecipientIds] = useState<string[]>([]);
  const [preview, setPreview] = useState<{ columns: string[]; rows: string[][]; rowCount: number; sheets?: Array<{ name: string; rowCount: number }> } | null>(null);
  const [saving, setSaving] = useState(false);

  const selectedModel = models.find((model) => model.code === modelCode) ?? null;

  const load = useCallback(async (orgId: string) => {
    if (!orgId) return;
    const [modelRes, templateRes, recipientRes] = await Promise.all([
      api.get('/api/reports/generator/models', { params: { organizationId: orgId } }),
      api.get('/api/reports/generator/templates', { params: { organizationId: orgId } }),
      api.get('/api/reports/generator/recipients', { params: { organizationId: orgId } }),
    ]);
    const nextModels = (modelRes.data?.data?.models ?? []) as ReportModel[];
    setModels(nextModels);
    setTemplates(templateRes.data?.data ?? []);
    setRecipients(recipientRes.data?.data ?? []);
    setModelCode((current) => current || nextModels[0]?.code || '');
  }, []);

  useEffect(() => {
    if (!organizationId) return;
    void load(organizationId).catch((error: unknown) => toast.error(apiError(error, t('common.error'))));
  }, [organizationId, load, t]);

  const resetForm = () => {
    setEditingId(null);
    setShowForm(false);
    setName('');
    setFieldKeys(['registration_number']);
    setActiveOnly(true);
    setWindowType('snapshot');
    setScheduleMode('manual');
    setDayOfMonth(3);
    setMonth(1);
    setRecipientIds([]);
    setPreview(null);
  };

  const startEdit = (template: TemplateRecord) => {
    setEditingId(template.id);
    setShowForm(true);
    setName(template.name);
    setModelCode(template.modelCode);
    setFieldKeys(template.fieldKeys);
    setActiveOnly(template.filters.activeOnly !== false);
    setWindowType(template.dataWindow.type);
    setScheduleMode(template.schedule?.type ?? 'manual');
    setDayOfMonth(template.schedule?.dayOfMonth ?? 3);
    setMonth(template.schedule?.month ?? 1);
    setRecipientIds(template.recipientUserIds);
    setPreview(null);
  };

  const toggleField = (key: string) => {
    if (key === 'registration_number') return;
    setFieldKeys((current) => (current.includes(key) ? current.filter((item) => item !== key) : [...current, key]));
  };

  const scheduleBody = useMemo(() => {
    if (scheduleMode === 'manual') return null;
    if (scheduleMode === 'monthly') return { type: 'monthly', dayOfMonth };
    return { type: 'yearly', month, dayOfMonth };
  }, [scheduleMode, dayOfMonth, month]);

  const save = async () => {
    if (!organizationId) return;
    setSaving(true);
    try {
      const body = {
        organizationId,
        modelCode,
        name,
        fieldKeys,
        activeOnly,
        dataWindow: { type: windowType },
        schedule: scheduleBody,
        recipientUserIds: recipientIds,
      };
      if (editingId) await api.put(`/api/reports/generator/templates/${editingId}`, body);
      else await api.post('/api/reports/generator/templates', body);
      toast.success(t('reportGenerator.saved'));
      resetForm();
      await load(organizationId);
    } catch (error: unknown) {
      toast.error(apiError(error, t('common.error')));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: number) => {
    try {
      await api.delete(`/api/reports/generator/templates/${id}`);
      toast.success(t('reportGenerator.deleted'));
      if (editingId === id) resetForm();
      await load(organizationId);
    } catch (error: unknown) {
      toast.error(apiError(error, t('common.error')));
    }
  };

  const download = async (id: number) => {
    try {
      const response = await api.get(`/api/reports/generator/templates/${id}/xlsx`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const disposition = String(response.headers?.['content-disposition'] ?? '');
      const named = /filename="([^"]+)"/.exec(disposition)?.[1];
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = named || `rapport-${id}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error: unknown) {
      toast.error(apiError(error, t('common.error')));
    }
  };

  const showPreview = async (id: number) => {
    try {
      const response = await api.get(`/api/reports/generator/templates/${id}/preview`);
      setPreview(response.data?.data ?? null);
    } catch (error: unknown) {
      toast.error(apiError(error, t('common.error')));
    }
  };

  return (
    <div className="p-4 bg-bgContainer rounded-lg h-full overflow-auto space-y-6">
      <div>
        <button type="button" onClick={() => navigate(ROUTES.SYSTEM_REPORTS)} className="inline-flex items-center gap-1 text-sm text-textSecondary mb-2">
          <ArrowLeftIcon className="w-4 h-4" />
          {t('common.back')}
        </button>
        <h1 className="text-2xl font-bold text-textDefault">{t('reportGenerator.title')}</h1>
        <p className="text-sm text-textTertiary mt-1">{t('reportGenerator.intro')}</p>
      </div>

      <div className="max-w-md">
        <label className="block text-sm font-medium text-textDefault mb-1">{t('reportGenerator.organization')}</label>
        <OrgTreeSelect value={organizationId} onChange={setOrganizationId} />
      </div>

      {organizationId && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-textDefault">{t('reportGenerator.templates')}</h2>
            <button
              type="button"
              className="px-3 py-2 bg-bgPrimary text-textLight rounded-lg text-sm"
              onClick={() => {
                resetForm();
                setShowForm(true);
                setModelCode(models[0]?.code ?? '');
              }}
            >
              {t('reportGenerator.newTemplate')}
            </button>
          </div>
          {templates.length === 0 ? (
            <p className="text-sm text-textSecondary">{t('reportGenerator.noTemplates')}</p>
          ) : (
            <ul className="divide-y divide-border border border-border rounded-lg">
              {templates.map((template) => (
                <li key={template.id} className="p-3 flex flex-wrap items-center gap-2 justify-between">
                  <span className="text-textDefault">{template.name}</span>
                  <span className="flex gap-2">
                    <button type="button" className="text-sm text-textSecondary" onClick={() => startEdit(template)}>{t('reportGenerator.edit')}</button>
                    <button type="button" className="text-sm text-textSecondary" onClick={() => void showPreview(template.id)}>{t('reportGenerator.preview')}</button>
                    <button type="button" className="text-sm text-textSecondary" onClick={() => void download(template.id)}>{t('reportGenerator.run')}</button>
                    <button type="button" className="text-sm text-red-600" onClick={() => void remove(template.id)}>{t('reportGenerator.delete')}</button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {showForm && (
        <div className="border border-border rounded-lg p-4 space-y-4">
          <label className="block text-sm">
            <span className="font-medium text-textDefault">{t('reportGenerator.name')}</span>
            <input value={name} onChange={(event) => setName(event.target.value)} className="mt-1 w-full max-w-md px-3 py-2 border border-border rounded-lg bg-bgContainer text-textDefault" />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-textDefault">{t('reportGenerator.model')}</span>
            {models.length === 0 ? (
              <p className="mt-1 text-textSecondary">{t('reportGenerator.noModels')}</p>
            ) : (
              <select value={modelCode} onChange={(event) => { setModelCode(event.target.value); setFieldKeys(['registration_number']); }} className="mt-1 w-full max-w-md px-3 py-2 border border-border rounded-lg bg-bgContainer text-textDefault">
                {models.map((model) => (
                  <option key={model.code} value={model.code}>{t(model.nameKey)}</option>
                ))}
              </select>
            )}
          </label>
          {selectedModel && (
            <div className="space-y-3">
              <h3 className="font-medium text-textDefault">{t('reportGenerator.fields.title')}</h3>
              {GROUPS.map((group) => {
                const fields = selectedModel.fields.filter((field) => field.group === group);
                if (fields.length === 0) return null;
                return (
                  <div key={group}>
                    <p className="text-xs uppercase text-textTertiary mb-1">{t(`reportGenerator.groups.${group}`)}</p>
                    <div className="flex flex-wrap gap-3">
                      {fields.map((field) => {
                        const disabled = field.key === 'registration_number' || (field.kind === 'period' && windowType === 'snapshot');
                        return (
                          <label key={field.key} className={`flex items-center gap-2 text-sm ${disabled && field.kind === 'period' ? 'text-textTertiary' : 'text-textDefault'}`}>
                            <input
                              type="checkbox"
                              checked={field.key === 'registration_number' || fieldKeys.includes(field.key)}
                              disabled={disabled}
                              onChange={() => toggleField(field.key)}
                            />
                            {t(field.labelKey)}
                            {field.key === 'registration_number' ? ` (${t('reportGenerator.lockedField')})` : ''}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <label className="flex items-center gap-2 text-sm text-textDefault">
            <input type="checkbox" checked={activeOnly} onChange={(event) => setActiveOnly(event.target.checked)} />
            {t('reportGenerator.activeOnly')}
          </label>
          <label className="block text-sm">
            <span className="font-medium text-textDefault">{t('reportGenerator.dataWindow')}</span>
            <select value={windowType} onChange={(event) => {
              const next = event.target.value;
              setWindowType(next);
              if (next === 'snapshot' && selectedModel) {
                const periodKeys = new Set(selectedModel.fields.filter((field) => field.kind === 'period').map((field) => field.key));
                setFieldKeys((current) => current.filter((key) => !periodKeys.has(key)));
              }
            }} className="mt-1 block px-3 py-2 border border-border rounded-lg bg-bgContainer text-textDefault">
              <option value="snapshot">{t('reportGenerator.snapshot')}</option>
              <option value="previous_calendar_week">{t('reportGenerator.previousWeek')}</option>
              <option value="previous_calendar_month">{t('reportGenerator.previousMonth')}</option>
              <option value="previous_calendar_quarter">{t('reportGenerator.previousQuarter')}</option>
              <option value="previous_calendar_tertial">{t('reportGenerator.previousTertial')}</option>
              <option value="previous_calendar_year">{t('reportGenerator.previousYear')}</option>
            </select>
          </label>
          <div className="space-y-2">
            <span className="block text-sm font-medium text-textDefault">{t('reportGenerator.schedule')}</span>
            <select value={scheduleMode} onChange={(event) => setScheduleMode(event.target.value as 'manual' | 'monthly' | 'yearly')} className="px-3 py-2 border border-border rounded-lg bg-bgContainer text-textDefault">
              <option value="manual">{t('reportGenerator.manual')}</option>
              <option value="monthly">{t('reportGenerator.monthly')}</option>
              <option value="yearly">{t('reportGenerator.yearly')}</option>
            </select>
            {scheduleMode !== 'manual' && (
              <div className="flex gap-3">
                {scheduleMode === 'yearly' && (
                  <label className="text-sm text-textDefault">
                    {t('reportGenerator.month')}
                    <input type="number" min={1} max={12} value={month} onChange={(event) => setMonth(Number(event.target.value))} className="ml-2 w-20 px-2 py-1 border border-border rounded bg-bgContainer" />
                  </label>
                )}
                <label className="text-sm text-textDefault">
                  {t('reportGenerator.day')}
                  <input type="number" min={1} max={28} value={dayOfMonth} onChange={(event) => setDayOfMonth(Number(event.target.value))} className="ml-2 w-20 px-2 py-1 border border-border rounded bg-bgContainer" />
                </label>
              </div>
            )}
            <p className="text-xs text-textTertiary">{t('reportGenerator.scheduleHelp')}</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-textDefault mb-1">{t('reportGenerator.recipients')}</h3>
            {recipients.length === 0 ? (
              <p className="text-sm text-textSecondary">{t('reportGenerator.noRecipients')}</p>
            ) : (
              <div className="max-h-48 overflow-auto space-y-1">
                {recipients.map((recipient) => (
                  <label key={recipient.id} className="flex items-center gap-2 text-sm text-textDefault">
                    <input
                      type="checkbox"
                      checked={recipientIds.includes(recipient.id)}
                      onChange={() => setRecipientIds((current) => current.includes(recipient.id) ? current.filter((id) => id !== recipient.id) : [...current, recipient.id])}
                    />
                    {recipient.name} ({recipient.email})
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <button type="button" disabled={saving} onClick={() => void save()} className="px-4 py-2 bg-bgPrimary text-textLight rounded-lg disabled:opacity-50">{t('reportGenerator.save')}</button>
            <button type="button" onClick={resetForm} className="px-4 py-2 bg-bgLight text-textDefault rounded-lg">{t('reportGenerator.cancel')}</button>
          </div>
        </div>
      )}

      {preview && (
        <div className="overflow-auto">
          <p className="text-sm text-textSecondary mb-2">
            {t('reportGenerator.previewCount')} {preview.rowCount}
            {preview.sheets && preview.sheets.length > 1
              ? `. ${preview.sheets.map((sheet) => `${sheet.name} (${sheet.rowCount})`).join(', ')}`
              : ''}
          </p>
          <table className="text-sm border-collapse">
            <thead>
              <tr>{preview.columns.map((column) => <th key={column} className="border border-border px-2 py-1 text-left">{column}</th>)}</tr>
            </thead>
            <tbody>
              {preview.rows.map((row, index) => (
                <tr key={index}>{row.map((cell, cellIndex) => <td key={cellIndex} className="border border-border px-2 py-1">{cell}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
